import React, { useEffect, memo, createElement } from 'react';
import { motion } from 'framer-motion';
import { Briefcase, Flag, Trophy, Users, Clock, Hash, ArrowRight, MessageCircle, Phone, Camera } from 'lucide-react';
import { Button } from '../components/ui/Button';
const audiences = [{
  icon: Briefcase,
  title: 'Businesses',
  description: 'Branded uniforms, company shirts, and promotional apparel for your team and customers.'
}, {
  icon: Trophy,
  title: 'Race Teams',
  description: 'Crew shirts, pit gear, and sponsor apparel built tough for the track.'
}, {
  icon: Flag,
  title: 'Events / 5Ks',
  description: 'Race shirts, volunteer tees, and event merchandise with fast group turnaround.'
}, {
  icon: Users,
  title: 'Organizations',
  description: 'Schools, churches, clubs, and nonprofits — spirit wear, fundraisers, and more.'
}];
const processSteps = [{
  step: '01',
  label: 'Request',
  description: 'Text us or send your idea'
}, {
  step: '02',
  label: 'Proof',
  description: 'We send a digital mockup'
}, {
  step: '03',
  label: 'Approve',
  description: 'Confirm design & pay'
}, {
  step: '04',
  label: 'Print',
  description: 'We print your order'
}, {
  step: '05',
  label: 'Pickup / Ship',
  description: 'Ready in 7–10 days'
}];
export function ScreenPrinting() {
  useEffect(() => {
    document.title = 'Screen Printing Michigan — Custom Shirts for Businesses & Teams | Print Atomic';
    const meta = document.querySelector('meta[name="description"]') || document.createElement('meta');
    meta.setAttribute('name', 'description');
    meta.setAttribute('content', 'Professional screen printing in Michigan for businesses, race teams, events, and organizations. No minimums, fast turnaround, and bold prints that last. Based in Morrice, MI.');
    if (!document.querySelector('meta[name="description"]')) {
      document.head.appendChild(meta);
    }
    return () => {
      document.title = 'Print Atomic';
    };
  }, []);
  return <div className="min-h-screen bg-atomic-black" data-id="element-1147">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden" data-id="element-1148">
        {/* Background image */}
        <div className="absolute inset-0 z-0" data-id="element-1149">
          <img src="https://cdn.magicpatterns.com/uploads/oM3MJHtEtSST3TtVQTW3oF/atomic-apparel-custom-motorsports-hoodie-tshirt-race-team-merch.jpg" alt="Custom motorsports screen printed hoodie and t-shirt for Kyle Maki race team" className="absolute inset-0 w-full h-full object-cover" data-id="element-1150" />
          <div className="absolute inset-0 bg-atomic-black/80" data-id="element-1151" />
          <div className="absolute inset-0 bg-gradient-to-b from-atomic-black/60 via-atomic-black/70 to-atomic-black/90" data-id="element-1152" />
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-1153">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-3xl mx-auto text-center" data-id="element-1154">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-1155">
              Screen{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-atomic-green to-lime-400" data-id="element-1156">
                Printing
              </span>
            </h1>

            <p className="text-xl text-gray-300 leading-relaxed mb-4" data-id="element-1157">
              Bold, durable prints for businesses, race teams, events, and
              organizations. No minimums — from a handful of shirts to a full
              production run.
            </p>

            <p className="text-sm text-gray-500 mb-8" data-id="element-1158">
              Michigan shop · statewide shipping · pickup available
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-1159">
              <a href="sms:+15174149232" className="w-full sm:w-auto" data-id="element-1160">
                <Button size="lg" className="w-full sm:w-auto gap-2" data-id="element-1161">
                  <MessageCircle className="w-5 h-5" data-id="element-1162" />
                  Text Us to Start
                </Button>
              </a>
              <a href="https://m.me/atomicMI" target="_blank" rel="noopener noreferrer" title="Facebook Message" aria-label="Facebook Message" className="w-full sm:w-auto" data-id="element-1163">
                <Button variant="outline" size="lg" className="w-full sm:w-auto gap-2" data-id="element-1164">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true" data-id="element-1165">
                    <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.2V22l2.95-1.62c.84.23 1.72.36 2.65.36h.25c5.64 0 9.75-4.13 9.75-9.66C20.75 6.13 17.64 2 12 2zm1.08 13.02l-2.5-2.67-4.87 2.67 5.36-5.69 2.56 2.67 4.81-2.67-5.36 5.69z" data-id="element-1166" />
                  </svg>
                  Facebook Message
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who We Print For */}
      <section className="py-20 bg-atomic-black border-t border-white/5" data-id="element-1167">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1168">
          <div className="max-w-5xl mx-auto" data-id="element-1169">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-1170">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1171">
                Who We Print For
              </h2>
              <p className="text-lg text-gray-300" data-id="element-1172">
                Screen printing for teams, businesses, and groups of every size
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1173">
              {audiences.map((audience, index) => <motion.div key={audience.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-atomic-charcoal rounded-xl p-6 border border-white/10" data-id="element-1174">
                  <div className="w-12 h-12 rounded-lg bg-atomic-green/10 flex items-center justify-center text-atomic-green mb-4 border border-atomic-green/20" data-id="element-1175">
                    <audience.icon size={24} data-id="element-1176" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-1177">
                    {audience.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed" data-id="element-1178">
                    {audience.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Work Examples */}
      <section className="py-20 bg-atomic-charcoal border-t border-white/5" data-id="element-1179">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1180">
          <div className="text-center mb-12" data-id="element-1181">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1182">
              Screen Print Examples
            </h2>
            <p className="text-lg text-gray-400" data-id="element-1183">
              Bold prints that last wash after wash
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-id="element-1184">
            {[{
            src: 'https://cdn.magicpatterns.com/uploads/ffsXvcwkVjznjeUT4uzeke/44F39316-0688-4D04-982E-96AA2A0938C7.jpg',
            alt: 'AFCO Racing custom screen printed apparel - hoodies and shirts',
            label: 'Racing Team Apparel'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/rt7ZvszK9Znns6vqVAvGuF/914a38b4-9421-4eb6-8740-ddc5ec25c26f.jpg',
            alt: 'Jernigan Racing custom screen printed merchandise display',
            label: 'Team Merchandise'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/9VkGcXP7d4DixMpNHxH8KQ/atomic-apparel-annual-event-shirt-kevin-adkins-memorial.jpg',
            alt: '4th Annual Kevin Adkins Memorial event shirt close-up',
            label: 'Event Shirts'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/cRDsupPp3D2QzMteBBezFr/custom-screen-printed-hoodies-hvac-michigan.jpg.jpg',
            alt: 'Daily Heating Air Conditioning custom screen printed hoodies',
            label: 'Business Workwear'
          }].map((image, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="group rounded-xl overflow-hidden border border-white/10 hover:border-atomic-green/30 transition-colors" data-id="element-1185">
                <div className="aspect-square overflow-hidden" data-id="element-1186">
                  <img src={image.src} alt={image.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1187" />
                </div>
                <div className="bg-atomic-black px-4 py-3" data-id="element-1188">
                  <span className="text-sm text-gray-300 font-medium" data-id="element-1189">
                    {image.label}
                  </span>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Turnaround + No Minimums */}
      <section className="py-16 bg-atomic-charcoal border-t border-white/5" data-id="element-1190">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1191">
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8" data-id="element-1192">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-atomic-black rounded-xl p-8 border border-white/10 text-center" data-id="element-1193">
              <div className="w-14 h-14 rounded-lg bg-atomic-green/10 flex items-center justify-center text-atomic-green mx-auto mb-5 border border-atomic-green/20" data-id="element-1194">
                <Clock size={28} data-id="element-1195" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-1196">Turnaround</h3>
              <p className="text-gray-300 leading-relaxed" data-id="element-1197">
                Typical{' '}
                <span className="text-atomic-green font-semibold" data-id="element-1198">
                  7–10 business days
                </span>{' '}
                after art approval & payment
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-atomic-black rounded-xl p-8 border border-white/10 text-center" data-id="element-1199">
              <div className="w-14 h-14 rounded-lg bg-atomic-green/10 flex items-center justify-center text-atomic-green mx-auto mb-5 border border-atomic-green/20" data-id="element-1200">
                <Hash size={28} data-id="element-1201" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3" data-id="element-1202">No Minimums</h3>
              <p className="text-gray-300 leading-relaxed" data-id="element-1203">
                Order as few or as many as you need. One shirt or a thousand —
                we print it all.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-atomic-black border-t border-white/5" data-id="element-1204">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1205">
          <div className="max-w-5xl mx-auto" data-id="element-1206">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-1207">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1208">
                How It Works
              </h2>
              <p className="text-lg text-gray-300" data-id="element-1209">
                Simple, straightforward process from start to finish
              </p>
            </motion.div>

            <div className="relative" data-id="element-1210">
              {/* Connection line */}
              <div className="hidden lg:block absolute top-10 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-atomic-green/30 to-transparent" data-id="element-1211" />

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6" data-id="element-1212">
                {processSteps.map((item, index) => <motion.div key={item.step} initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="text-center relative" data-id="element-1213">
                    <div className="w-20 h-20 rounded-full bg-atomic-charcoal border-2 border-atomic-green/30 flex items-center justify-center mx-auto mb-4 relative z-10" data-id="element-1214">
                      <span className="text-atomic-green font-bold text-lg" data-id="element-1215">
                        {item.step}
                      </span>
                    </div>
                    <h3 className="text-white font-bold mb-1" data-id="element-1216">{item.label}</h3>
                    <p className="text-gray-500 text-sm" data-id="element-1217">{item.description}</p>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Block */}
      <section className="py-20 bg-atomic-charcoal border-t border-white/5" data-id="element-1218">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1219">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="max-w-3xl mx-auto text-center" data-id="element-1220">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1221">
              Ready to Start Your Screen Print Order?
            </h2>
            <p className="text-lg text-gray-300 mb-8" data-id="element-1222">
              Text us your idea, send a Facebook message, or give us a call. We
              typically respond within minutes.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-1223">
              {/* Text Us */}
              <a href="sms:+15174149232" className="w-full sm:w-auto" data-id="element-1224">
                <Button size="lg" className="w-full sm:w-auto gap-2" data-id="element-1225">
                  <MessageCircle className="w-5 h-5" data-id="element-1226" />
                  Text Us
                </Button>
              </a>

              {/* Facebook Message */}
              <a href="https://m.me/atomicMI" target="_blank" rel="noopener noreferrer" title="Facebook Message" aria-label="Facebook Message" className="w-full sm:w-auto" data-id="element-1227">
                <Button variant="outline" size="lg" className="w-full sm:w-auto gap-2" data-id="element-1228">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true" data-id="element-1229">
                    <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.2V22l2.95-1.62c.84.23 1.72.36 2.65.36h.25c5.64 0 9.75-4.13 9.75-9.66C20.75 6.13 17.64 2 12 2zm1.08 13.02l-2.5-2.67-4.87 2.67 5.36-5.69 2.56 2.67 4.81-2.67-5.36 5.69z" data-id="element-1230" />
                  </svg>
                  Facebook Message
                </Button>
              </a>

              {/* Call */}
              <a href="tel:+15174149232" className="w-full sm:w-auto" data-id="element-1231">
                <Button variant="ghost" size="lg" className="w-full sm:w-auto gap-2" data-id="element-1232">
                  <Phone className="w-5 h-5" data-id="element-1233" />
                  Call 517-414-9232
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-atomic-black border-t border-white/5" data-id="element-1234">
        <div className="container mx-auto px-4 md:px-6" data-id="element-1235">
          <div className="max-w-3xl mx-auto" data-id="element-1236">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-1237">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1238">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-300" data-id="element-1239">
                Common questions about screen printing
              </p>
            </motion.div>

            <div className="space-y-6" data-id="element-1240">
              {[{
              question: 'What is the typical turnaround time?',
              answer: 'Typical 7–10 business days after art approval & payment. Rush options may be available depending on the project — just let us know your deadline.'
            }, {
              question: 'Is there a minimum order?',
              answer: 'No minimums. Whether you need one shirt or a thousand, we print it all. Screen printing is ideal for any quantity.'
            }, {
              question: 'What files do you need for artwork?',
              answer: 'Vector files (AI, EPS, SVG) work best for screen printing. High-resolution PNG or PDF files are also accepted. If you only have a rough idea or low-res image, we can help with design assistance.'
            }, {
              question: 'Do you provide proofs before printing?',
              answer: "Yes — every order gets a digital proof (mockup) before we print. You'll review and approve the design, colors, and placement before anything goes to press. We don't print until you say go."
            }].map((faq, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="bg-atomic-charcoal rounded-xl p-6 border border-white/10" data-id="element-1241">
                  <h3 className="text-lg font-bold text-white mb-3" data-id="element-1242">
                    {faq.question}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1243">{faq.answer}</p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}